import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ImageModal from '../components/ImageModal';
import { uploadImage } from '../utils/imageUtils';
import { enhanceImageUrl } from '../utils/imageEnhancer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Profile = () => {
  const { user, token, refreshUser, logout, updateUsername } = useAuth();
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
    bio: '',
    location: '',
    timezone: '',
    avatar: null,
    secret_mystery_name: '',
    website: '',
    facebook_profile: '',
    twitter_profile: '',
    linkedin_profile: '',
    instagram_profile: ''
  });
  const [affirmations, setAffirmations] = useState([]);
  const [affirmationImages, setAffirmationImages] = useState([]);
  const [affirmationPrivacy, setAffirmationPrivacy] = useState([]);  // Privacy setting for each affirmation
  const [privacySettings, setPrivacySettings] = useState({
    anonymize_in_reality_garden: false,
    show_location: true,
    show_email: false
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [usernameInput, setUsernameInput] = useState('');
  const [usernameSaving, setUsernameSaving] = useState(false);
  const [usernameMsg, setUsernameMsg] = useState(null); // {type:'success'|'error', text}
  const [activeTab, setActiveTab] = useState('profile');
  const [isPublic, setIsPublic] = useState(true);
  const [shareCopied, setShareCopied] = useState(false);
  const [newAffirmation, setNewAffirmation] = useState('');
  const [newAffirmationImage, setNewAffirmationImage] = useState('');
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [showAffirmationModal, setShowAffirmationModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  
  // Mystery Name state
  const [mysteryNameAvailability, setMysteryNameAvailability] = useState(null);
  const [mysteryNameSuggestions, setMysteryNameSuggestions] = useState([]);
  const [mysteryNameChecking, setMysteryNameChecking] = useState(false);
  const [mysteryNameCheckTimeout, setMysteryNameCheckTimeout] = useState(null);

  useEffect(() => {
    if (user) {
      // Always fetch fresh user data when Profile loads
      fetchUserData();
    }
  }, [user]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (mysteryNameCheckTimeout) {
        clearTimeout(mysteryNameCheckTimeout);
      }
    };
  }, [mysteryNameCheckTimeout]);

  const fetchUserData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/auth/me`, { headers });
      const freshUser = response.data;
      
      // Update the profile form with fresh data
      setProfileData({
        first_name: freshUser.first_name || '',
        last_name: freshUser.last_name || '',
        bio: freshUser.bio || '',
        location: freshUser.location || '',
        timezone: freshUser.timezone || '',
        avatar: freshUser.avatar || '',
        secret_mystery_name: freshUser.secret_mystery_name || '',
        website: freshUser.website || '',
        facebook_profile: freshUser.facebook_profile || '',
        twitter_profile: freshUser.twitter_profile || '',
        linkedin_profile: freshUser.linkedin_profile || '',
        instagram_profile: freshUser.instagram_profile || ''
      });
      setAffirmations(freshUser.affirmations || []);
      setAffirmationImages(freshUser.affirmation_images || []);
      setAffirmationPrivacy(freshUser.affirmation_privacy || []);
      setPrivacySettings(freshUser.privacy_settings || privacySettings);
      setIsPublic(freshUser.is_public !== false);
      setUsernameInput(freshUser.username || '');
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle mystery name separately with special validation
    if (name === 'secret_mystery_name') {
      return; // This will be handled by handleMysteryNameChange
    }
    
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveUsername = async () => {
    setUsernameMsg(null);
    const desired = (usernameInput || '').trim();
    if (desired === (user?.username || '')) {
      setUsernameMsg({ type: 'error', text: 'That is already your username.' });
      return;
    }
    setUsernameSaving(true);
    const result = await updateUsername(desired);
    setUsernameSaving(false);
    if (result.success) {
      setUsernameMsg({ type: 'success', text: 'Username updated!' });
    } else {
      setUsernameMsg({ type: 'error', text: result.error });
    }
  };

  const handlePrivacyChange = (e) => {
    const { name, checked } = e.target;
    setPrivacySettings(prev => ({
      ...prev,
      [name]: checked
    }));
  };



  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploading(true);
      try {
        // Upload to Cloudinary with profile image quality settings
        const result = await uploadImage(file, 'cloudinary', { 
          folder: 'mhb_avatars',
          purpose: 'profile_image',
          boostLevel: 'premium' // Good quality for profile images
        });
        setProfileData(prev => ({
          ...prev,
          avatar: result.url
        }));
      } catch (error) {
        console.error('Error uploading avatar:', error);
        alert('Failed to upload avatar. Please try again.');
      } finally {
        setUploading(false);
      }
    }
  };

  const addAffirmation = () => {
    if (newAffirmation.trim() && affirmations.length < 10) {
      setAffirmations([...affirmations, newAffirmation.trim()]);
      // Add privacy setting for new affirmation (default to public)
      setAffirmationPrivacy([...affirmationPrivacy, false]);
      setNewAffirmation('');
    }
  };

  const startEdit = (index) => {
    setEditingIndex(index);
    setEditingText(affirmations[index]);
  };

  const saveEdit = () => {
    if (editingText.trim()) {
      const updated = [...affirmations];
      updated[editingIndex] = editingText.trim();
      setAffirmations(updated);
    }
    setEditingIndex(null);
    setEditingText('');
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setEditingText('');
  };

  const deleteAffirmation = (index) => {
    if (window.confirm('Are you sure you want to delete this affirmation?')) {
      const updated = affirmations.filter((_, i) => i !== index);
      setAffirmations(updated);
      
      // Also update privacy settings array
      const updatedPrivacy = affirmationPrivacy.filter((_, i) => i !== index);
      setAffirmationPrivacy(updatedPrivacy);
    }
  };

  const toggleAffirmationPrivacy = (index) => {
    const updated = [...affirmationPrivacy];
    // Ensure array is long enough
    while (updated.length <= index) {
      updated.push(false); // Default to public
    }
    updated[index] = !updated[index]; // Toggle privacy
    setAffirmationPrivacy(updated);
  };

  // REDESIGNED: Cleaner image upload with immediate state updates
  const handleAffirmationImageUpload = async (e, imageArrayIndex = null) => {
    const file = e.target.files[0];
    if (file) {
      // Check if user is authenticated
      if (!token) {
        const noAuthNotification = document.createElement('div');
        noAuthNotification.textContent = '🔒 Please log in to upload images';
        noAuthNotification.className = 'fixed top-4 right-4 bg-orange-500 text-white px-4 py-2 rounded-lg shadow-lg z-50';
        document.body.appendChild(noAuthNotification);
        
        setTimeout(() => {
          if (noAuthNotification.parentNode) {
            document.body.removeChild(noAuthNotification);
          }
        }, 3000);
        return;
      }

      setUploading(true);
      try {
        // Upload to Cloudinary with maximum quality settings for vision board
        const result = await uploadImage(file, 'cloudinary', { 
          folder: 'mhb_affirmations',
          purpose: 'vision_board', // This will preserve original quality
          boostLevel: 'vip' // Backup quality setting
        });
        
        if (imageArrayIndex !== null) {
          // Update existing affirmation image - immediate state update
          const updated = [...affirmationImages];
          updated[imageArrayIndex] = result.url;
          setAffirmationImages(updated);
          // Show immediate success feedback
          e.target.parentElement.style.background = '#10b981';
          setTimeout(() => {
            e.target.parentElement.style.background = '';
          }, 1000);
        } else {
          // Adding new affirmation image
          setNewAffirmationImage(result.url);
        }
        
      } catch (error) {
        console.error('Error uploading affirmation image:', error);
        
        // Check if it's an authentication error
        if (error.message.includes('Invalid token') || error.message.includes('401')) {
          // Show authentication error notification with logout option
          const authNotification = document.createElement('div');
          authNotification.innerHTML = `
            <div>
              <div class="font-semibold text-white mb-2">🔒 Session Expired</div>
              <div class="text-red-100 text-sm mb-3">Your session has expired. Please log in again to upload images.</div>
              <button onclick="window.handleAuthLogout()" class="bg-white text-red-500 px-3 py-1 rounded font-medium hover:bg-red-50 transition-colors">
                Log Out & Refresh
              </button>
            </div>
          `;
          authNotification.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-4 rounded-lg shadow-xl z-50 max-w-sm';
          document.body.appendChild(authNotification);
          
          // Create global logout handler
          window.handleAuthLogout = () => {
            logout();
            navigate('/login');
            if (authNotification.parentNode) {
              document.body.removeChild(authNotification);
            }
          };
          
          // Auto-remove after 10 seconds
          setTimeout(() => {
            if (authNotification.parentNode) {
              document.body.removeChild(authNotification);
            }
            delete window.handleAuthLogout;
          }, 10000);
        } else {
          // Show general error notification
          const errorNotification = document.createElement('div');
          errorNotification.textContent = '❌ Failed to upload image. Please try again.';
          errorNotification.className = 'fixed top-4 right-4 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg z-50';
          document.body.appendChild(errorNotification);
          
          setTimeout(() => {
            if (errorNotification.parentNode) {
              document.body.removeChild(errorNotification);
            }
          }, 3000);
        }
      } finally {
        setUploading(false);
      }
    }
  };

  // Mystery Name Functions
  const checkMysteryNameAvailability = async (name) => {
    if (!name || !name.trim()) {
      setMysteryNameAvailability(null);
      setMysteryNameSuggestions([]);
      return;
    }

    setMysteryNameChecking(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/mystery-name/check/${encodeURIComponent(name.trim())}`, { headers });
      
      setMysteryNameAvailability(response.data);
      
      // If not available, get suggestions
      if (!response.data.available && response.data.message !== "This is your current mystery name") {
        const suggestionsResponse = await axios.get(`${API}/mystery-name/suggestions/${encodeURIComponent(name.trim())}`, { headers });
        setMysteryNameSuggestions(suggestionsResponse.data.suggestions);
      } else {
        setMysteryNameSuggestions([]);
      }
    } catch (error) {
      console.error('Error checking mystery name availability:', error);
      setMysteryNameAvailability({ available: false, message: "Error checking availability" });
      setMysteryNameSuggestions([]);
    } finally {
      setMysteryNameChecking(false);
    }
  };

  const handleMysteryNameChange = (e) => {
    const value = e.target.value;
    setProfileData(prev => ({ ...prev, secret_mystery_name: value }));

    // Clear previous timeout
    if (mysteryNameCheckTimeout) {
      clearTimeout(mysteryNameCheckTimeout);
    }

    // Set new timeout for checking availability
    const newTimeout = setTimeout(() => {
      checkMysteryNameAvailability(value);
    }, 500); // Wait 500ms after user stops typing

    setMysteryNameCheckTimeout(newTimeout);
  };

  const selectMysteryNameSuggestion = (suggestion) => {
    setProfileData(prev => ({ ...prev, secret_mystery_name: suggestion }));
    setMysteryNameAvailability({ available: true, message: "Available" });
    setMysteryNameSuggestions([]);
  };

  // REDESIGNED: Robust save with proper state management
  const saveProfile = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Prepare clean payload
      const cleanPayload = {
        first_name: profileData.first_name,
        last_name: profileData.last_name,
        bio: profileData.bio,
        avatar: profileData.avatar,
        location: profileData.location,
        timezone: profileData.timezone,
        secret_mystery_name: profileData.secret_mystery_name,
        website: profileData.website,
        facebook_profile: profileData.facebook_profile,
        twitter_profile: profileData.twitter_profile,
        linkedin_profile: profileData.linkedin_profile,
        instagram_profile: profileData.instagram_profile,
        affirmations,
        affirmation_images: affirmationImages,
        affirmation_privacy: affirmationPrivacy,
        privacy_settings: privacySettings,
        is_public: isPublic
      };
      
      // Update profile data
      const response = await axios.put(`${API}/users/profile`, cleanPayload, { headers });

      // REDESIGNED: Immediate state updates + context refresh
      // 1. Update local state immediately with response data
      const updatedUserData = response.data;
      setAffirmationImages(updatedUserData.affirmation_images || []);
      
      // 2. Refresh the auth context to propagate changes to all components
      await refreshUser();
      
      // 3. Success feedback
      alert('Profile saved successfully!');
      
    } catch (error) {
      console.error('Error updating profile:', error);
      
      // Check if it's an Akismet spam detection error
      if (error.response?.status === 400 && error.response?.data?.detail?.reason === 'spam_detected') {
        const errorData = error.response.data.detail;
        alert(`${errorData.message}\n\n${errorData.akismet_info}`);
      } else {
        alert(`Failed to update profile: ${error.response?.data?.detail || error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profile Info', icon: '👤' },
    { id: 'affirmations', label: 'Affirmations', icon: '✨' },
    { id: 'privacy', label: 'Privacy', icon: '🛡️' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100">
      <div className="max-w-4xl mx-auto p-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-emerald-800 mb-2">Your Profile</h1>
          <p className="text-emerald-600">Shape your digital presence with intention</p>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-1 bg-white/20 backdrop-blur-lg rounded-2xl p-2 mb-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 px-6 py-3 rounded-xl font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-white text-emerald-700 shadow-lg'
                  : 'text-emerald-600 hover:bg-white/30'
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="bg-white/80 backdrop-blur-lg rounded-3xl p-8 shadow-xl">
          
          {/* Profile Info Tab */}
          {activeTab === 'profile' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-semibold text-emerald-800 mb-6">Profile Information</h2>
              
              {/* Avatar Upload */}
              <div className="flex items-center space-x-6">
                <div className="relative">
                  <div className="w-24 h-24 rounded-full overflow-hidden bg-emerald-200 flex items-center justify-center">
                    {profileData.avatar ? (
                      <img src={profileData.avatar} alt="Avatar" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-emerald-600 text-2xl">👤</span>
                    )}
                  </div>
                  <label className="absolute bottom-0 right-0 bg-emerald-600 text-white rounded-full p-2 cursor-pointer hover:bg-emerald-700">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleAvatarUpload}
                      className="hidden"
                    />
                    📷
                  </label>
                </div>
                <div>
                  <h3 className="font-medium text-gray-700">Profile Picture</h3>
                  <p className="text-sm text-gray-500">Click the camera to upload a new image</p>
                </div>
              </div>

              {/* Username */}
              <div data-testid="username-section">
                <label className="block text-sm font-medium text-gray-700 mb-2">Username</label>
                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="text"
                    data-testid="username-input"
                    value={usernameInput}
                    onChange={(e) => { setUsernameInput(e.target.value); setUsernameMsg(null); }}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="Choose a unique username"
                  />
                  <button
                    type="button"
                    data-testid="save-username-button"
                    onClick={handleSaveUsername}
                    disabled={usernameSaving || !usernameInput.trim()}
                    className="px-6 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors font-medium disabled:opacity-50 whitespace-nowrap"
                  >
                    {usernameSaving ? 'Saving...' : 'Save Username'}
                  </button>
                </div>
                {usernameMsg && (
                  <p
                    data-testid="username-message"
                    className={`mt-2 text-sm ${usernameMsg.type === 'success' ? 'text-emerald-600' : 'text-red-600'}`}
                  >
                    {usernameMsg.text}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-400">3–30 characters. Letters, numbers, and . _ - only. Must be unique.</p>
              </div>

              {/* Form Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                  <input
                    type="text"
                    name="first_name"
                    value={profileData.first_name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="Your first name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                  <input
                    type="text"
                    name="last_name"
                    value={profileData.last_name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="Your last name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Bio</label>
                <textarea
                  name="bio"
                  value={profileData.bio}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="Share your journey, wisdom, or what brings you peace..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                <input
                  type="text"
                  name="location"
                  value={profileData.location}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="Your city, state/region"
                />
              </div>

              {/* Timezone Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  🌍 Timezone
                </label>
                <select
                  name="timezone"
                  value={profileData.timezone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">Select your timezone</option>
                  <optgroup label="🇺🇸 United States">
                    <option value="America/New_York">Eastern Time (New York, Miami, Atlanta)</option>
                    <option value="America/Chicago">Central Time (Chicago, Dallas, Austin)</option>
                    <option value="America/Denver">Mountain Time (Denver, Phoenix, Salt Lake City)</option>
                    <option value="America/Los_Angeles">Pacific Time (Los Angeles, San Francisco, Seattle)</option>
                    <option value="America/Anchorage">Alaska Time (Anchorage)</option>
                    <option value="Pacific/Honolulu">Hawaii Time (Honolulu)</option>
                  </optgroup>
                  <optgroup label="🇨🇦 Canada">
                    <option value="America/Toronto">Eastern Time (Toronto, Montreal, Ottawa)</option>
                    <option value="America/Winnipeg">Central Time (Winnipeg, Saskatoon)</option>
                    <option value="America/Edmonton">Mountain Time (Calgary, Edmonton)</option>
                    <option value="America/Vancouver">Pacific Time (Vancouver, Victoria)</option>
                  </optgroup>
                  <optgroup label="🇬🇧 United Kingdom">
                    <option value="Europe/London">GMT/BST (London, Edinburgh, Cardiff)</option>
                  </optgroup>
                  <optgroup label="🇪🇺 Europe">
                    <option value="Europe/Paris">Central European Time (Paris, Berlin, Madrid)</option>
                    <option value="Europe/Rome">Central European Time (Rome, Milan)</option>
                    <option value="Europe/Amsterdam">Central European Time (Amsterdam, Brussels)</option>
                    <option value="Europe/Stockholm">Central European Time (Stockholm, Oslo)</option>
                    <option value="Europe/Zurich">Central European Time (Zurich, Vienna)</option>
                  </optgroup>
                  <optgroup label="🇦🇺 Australia">
                    <option value="Australia/Sydney">Eastern Time (Sydney, Melbourne, Brisbane)</option>
                    <option value="Australia/Adelaide">Central Time (Adelaide)</option>
                    <option value="Australia/Perth">Western Time (Perth)</option>
                  </optgroup>
                  <optgroup label="🌏 Asia">
                    <option value="Asia/Tokyo">Japan Time (Tokyo, Osaka)</option>
                    <option value="Asia/Shanghai">China Time (Beijing, Shanghai)</option>
                    <option value="Asia/Hong_Kong">Hong Kong Time</option>
                    <option value="Asia/Singapore">Singapore Time</option>
                    <option value="Asia/Mumbai">India Time (Mumbai, Delhi, Bangalore)</option>
                    <option value="Asia/Dubai">UAE Time (Dubai, Abu Dhabi)</option>
                  </optgroup>
                  <optgroup label="🌎 Other">
                    <option value="UTC">UTC (Coordinated Universal Time)</option>
                    <option value="America/Mexico_City">Mexico Time (Mexico City)</option>
                    <option value="America/Sao_Paulo">Brazil Time (São Paulo, Rio de Janeiro)</option>
                    <option value="Africa/Cairo">Egypt Time (Cairo)</option>
                    <option value="Africa/Johannesburg">South Africa Time (Johannesburg, Cape Town)</option>
                  </optgroup>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  ⏰ Your daily ritual will reset at midnight in your timezone. This ensures proper timing for your morning practice.
                </p>
              </div>

              {/* Secret Mystery Name for Reality Garden */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  🔮 Secret Mystery Name
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="secret_mystery_name"
                    value={profileData.secret_mystery_name}
                    onChange={handleMysteryNameChange}
                    className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 pr-10 ${
                      mysteryNameAvailability?.available === true 
                        ? 'border-green-300 focus:ring-green-500' 
                        : mysteryNameAvailability?.available === false 
                        ? 'border-red-300 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-purple-500'
                    }`}
                    placeholder="Your mystical identity for Reality Garden affirmations..."
                  />
                  {/* Status indicator */}
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    {mysteryNameChecking ? (
                      <div className="w-5 h-5 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin"></div>
                    ) : mysteryNameAvailability?.available === true ? (
                      <span className="text-green-500 text-xl">✓</span>
                    ) : mysteryNameAvailability?.available === false ? (
                      <span className="text-red-500 text-xl">✗</span>
                    ) : null}
                  </div>
                </div>

                {/* Availability status message */}
                {mysteryNameAvailability && (
                  <p className={`text-sm mt-1 font-medium ${
                    mysteryNameAvailability.available ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {mysteryNameAvailability.message}
                  </p>
                )}

                {/* Suggestions when name is taken */}
                {mysteryNameSuggestions.length > 0 && (
                  <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-xl">
                    <p className="text-sm font-medium text-purple-800 mb-2">✨ Try these alternatives:</p>
                    <div className="flex flex-wrap gap-2">
                      {mysteryNameSuggestions.map((suggestion, index) => (
                        <button
                          key={index}
                          onClick={() => selectMysteryNameSuggestion(suggestion)}
                          className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm hover:bg-purple-200 transition-colors cursor-pointer border border-purple-300"
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <p className="text-sm text-purple-600 mt-2">
                  ✨ <strong>Private & Mystical:</strong> This name will be shown on your affirmations in Reality Garden instead of your username. Only you can see and edit this field - it creates an energetic connection while keeping your affirmations anonymous.
                </p>
              </div>

              {/* Social Media & Website Links */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">🌐 Social Links (Optional)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  
                  {/* Website */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      🌐 Your Website
                    </label>
                    <input
                      type="url"
                      name="website"
                      value={profileData.website}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      placeholder="https://yourwebsite.com"
                    />
                  </div>

                  {/* Facebook */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      📘 Facebook Profile
                    </label>
                    <input
                      type="url"
                      name="facebook_profile"
                      value={profileData.facebook_profile}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="https://facebook.com/yourprofile"
                    />
                  </div>

                  {/* Twitter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      🐦 Twitter Profile
                    </label>
                    <input
                      type="url"
                      name="twitter_profile"
                      value={profileData.twitter_profile}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500"
                      placeholder="https://twitter.com/yourusername"
                    />
                  </div>

                  {/* LinkedIn */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      💼 LinkedIn Profile
                    </label>
                    <input
                      type="url"
                      name="linkedin_profile"
                      value={profileData.linkedin_profile}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-700"
                      placeholder="https://linkedin.com/in/yourprofile"
                    />
                  </div>

                  {/* Instagram */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      📷 Instagram Profile
                    </label>
                    <input
                      type="url"
                      name="instagram_profile"
                      value={profileData.instagram_profile}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500"
                      placeholder="https://instagram.com/yourusername"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Affirmations Tab */}
          {activeTab === 'affirmations' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-semibold text-emerald-800">Affirmations</h2>
                <span className="text-sm text-emerald-600">{affirmations.length}/10</span>
              </div>
              
              <div className="space-y-4">
                <p className="text-gray-600 mb-4">
                  Create up to 10 personal reality checks that will guide your daily protocol. 
                  You can also add up to 4 vision board images for each. The first image of each section will be the one that shows up with the statement in the Reality Garden and randomized and anonymized community share meditation so do not use anything too identifying or personal. The other three will be seen by you and you alone. We also have a toggle for any statement you feel is too personal and you don't wish for it to be shared in the reality garden. But you should keep many open to receive that good old quantum meat computer consciousness/observer boost.
                </p>
                
                {/* Important Note about First Image Usage */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                  <div className="flex items-start space-x-3">
                    <span className="text-blue-500 text-xl flex-shrink-0">💡</span>
                    <div>
                      <h4 className="font-medium text-blue-800 mb-2">Vision Board Display</h4>
                      <p className="text-blue-700 text-sm leading-relaxed">
                        <strong>Important:</strong> The first vision board image you upload will be displayed during 
                        the "The world we thrive in" section of your Morning Manifestation Protocol, as well as 
                        in the Reality Garden affirmation displays. Choose an image that represents your highest 
                        vision for yourself and the world.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Existing Affirmations */}
              <div className="space-y-4">
                {affirmations.map((affirmation, index) => (
                  <div key={index} className="p-6 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl border border-emerald-200">
                    {editingIndex === index ? (
                      <div className="space-y-3">
                        <textarea
                          value={editingText}
                          onChange={(e) => setEditingText(e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          rows={3}
                        />
                        <div className="flex space-x-2">
                          <button
                            onClick={saveEdit}
                            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                          >
                            Save
                          </button>
                          <button
                            onClick={cancelEdit}
                            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="flex justify-between items-start mb-4">
                          <p className="text-emerald-800 font-medium flex-1">{affirmation}</p>
                          <div className="flex space-x-2 ml-4">
                            <button
                              onClick={() => toggleAffirmationPrivacy(index)}
                              className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                                affirmationPrivacy[index] 
                                  ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                                  : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                              }`}
                              title={affirmationPrivacy[index] ? 'Click to make public' : 'Click to make private'}
                            >
                              {affirmationPrivacy[index] ? 'Private' : 'Public'}
                            </button>
                            <button
                              onClick={() => startEdit(index)}
                              className="text-emerald-600 hover:text-emerald-800"
                            >
                              ✏️
                            </button>
                            <button
                              onClick={() => deleteAffirmation(index)}
                              className="text-red-500 hover:text-red-700"
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                        
                        {/* Vision Board Images (2x2 grid) */}
                        <div className="grid grid-cols-2 gap-3">
                          {[0, 1, 2, 3].map((imgIndex) => {
                            const imageArrayIndex = index * 4 + imgIndex;
                            const image = affirmationImages[imageArrayIndex];
                            return (
                              <div key={imgIndex} className="relative">
                                {image ? (
                                  <div className="group relative">
                                    <img 
                                      src={enhanceImageUrl(image)} 
                                      alt={`Vision ${imageArrayIndex + 1}`}
                                      className="w-full aspect-square object-contain rounded-xl border border-emerald-300 cursor-pointer hover:brightness-110 transition-all duration-300 bg-gray-50"
                                      onClick={() => {
                                        setSelectedImage({ url: image, title: `Vision ${imageArrayIndex + 1}` });
                                        setShowImageModal(true);
                                      }}
                                      loading="lazy"
                                    />
                                    {/* Hover overlay for expand hint */}
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 rounded-xl flex items-center justify-center opacity-0 group-hover:opacity-100 pointer-events-none">
                                      <div className="bg-white/90 backdrop-blur-sm px-2 py-1 rounded text-gray-800 font-medium text-xs">
                                        🔍 Click to expand
                                      </div>
                                    </div>
                                    {/* Change button overlay */}
                                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                      <label className="cursor-pointer text-white text-xs bg-emerald-600 px-2 py-1 rounded hover:bg-emerald-700">
                                        Change
                                        <input
                                          type="file"
                                          accept="image/*"
                                          className="hidden"
                                          onChange={(e) => handleAffirmationImageUpload(e, imageArrayIndex)}
                                        />
                                      </label>
                                    </div>
                                  </div>
                                ) : (
                                  <label className="w-full aspect-square border-2 border-dashed border-emerald-300 rounded-xl flex items-center justify-center cursor-pointer hover:border-emerald-500 transition-colors">
                                    <div className="text-center">
                                      <span className="text-emerald-600 text-2xl block">📷</span>
                                      <span className="text-emerald-600 text-sm">Add Vision</span>
                                    </div>
                                    <input
                                      type="file"
                                      accept="image/*"
                                      className="hidden"
                                      onChange={(e) => handleAffirmationImageUpload(e, imageArrayIndex)}
                                    />
                                  </label>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
                
                {/* Privacy Control Info */}
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-6">
                  <div className="flex items-start space-x-3">
                    <span className="text-purple-500 text-xl flex-shrink-0">🔒</span>
                    <div>
                      <h4 className="font-medium text-purple-800 mb-2">Privacy Controls</h4>
                      <p className="text-purple-700 text-sm leading-relaxed">
                        <strong>Public:</strong> Reality check appears in community Morning Manifestation and Reality Garden. 
                        <strong>Private:</strong> Reality check stays personal - only visible to you. 
                        Toggle privacy for each one individually. We recommend keeping most public for that quantum consciousness boost!
                      </p>
                    </div>
                  </div>
                </div>

              {/* Add New Affirmation */}
              {affirmations.length < 10 && (
                <div className="p-6 border-2 border-dashed border-emerald-300 rounded-2xl">
                  <h3 className="font-medium text-emerald-700 mb-3">Add New Affirmation</h3>
                  <div className="space-y-3">
                    <textarea
                      value={newAffirmation}
                      onChange={(e) => setNewAffirmation(e.target.value)}
                      placeholder="I am..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      rows={3}
                    />
                    <button
                      onClick={addAffirmation}
                      disabled={!newAffirmation.trim()}
                      className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Add Affirmation
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Privacy Tab */}
          {activeTab === 'privacy' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-semibold text-emerald-800 mb-6">Privacy Settings</h2>

              {/* Public "My Life" Page */}
              <div className="p-4 bg-purple-50 rounded-xl border border-purple-200 space-y-4" data-testid="public-profile-settings">
                <div className="flex items-center justify-between">
                  <div className="pr-4">
                    <h3 className="font-medium text-purple-800">Public "My Life" Page</h3>
                    <p className="text-sm text-purple-600">When enabled, anyone can view your public affirmations, vision board and public posts at your shareable link.</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isPublic}
                      onChange={(e) => setIsPublic(e.target.checked)}
                      className="sr-only peer"
                      data-testid="toggle-public-profile"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                  </label>
                </div>
                {isPublic && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <code className="flex-1 text-xs sm:text-sm bg-white border border-purple-200 rounded-lg px-3 py-2 text-purple-700 truncate" data-testid="public-profile-url">
                      {`${window.location.origin}/${usernameInput || (user && user.username) || ''}`}
                    </code>
                    <button
                      type="button"
                      onClick={() => {
                        const url = `${window.location.origin}/${usernameInput || (user && user.username) || ''}`;
                        navigator.clipboard.writeText(url);
                        setShareCopied(true);
                        setTimeout(() => setShareCopied(false), 2000);
                      }}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors whitespace-nowrap"
                      data-testid="share-my-page-btn"
                    >
                      {shareCopied ? 'Copied!' : 'Share My Page'}
                    </button>
                  </div>
                )}
              </div>

              <div className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-xl">
                  <div>
                    <h3 className="font-medium text-emerald-800">Allow your visions to appear in the community Reality Garden</h3>
                    <p className="text-sm text-emerald-600">When enabled, your Reality Garden entries will be visible to the community</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="show_in_reality_garden"
                      checked={!privacySettings.anonymize_in_reality_garden}
                      onChange={(e) => setPrivacySettings(prev => ({
                        ...prev,
                        anonymize_in_reality_garden: !e.target.checked
                      }))}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-xl">
                  <div>
                    <h3 className="font-medium text-emerald-800">Anonymize in Reality Garden</h3>
                    <p className="text-sm text-emerald-600">When enabled, your Reality Garden entries will appear without your name</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="anonymize_in_reality_garden"
                      checked={privacySettings.anonymize_in_reality_garden}
                      onChange={handlePrivacyChange}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-xl">
                  <div>
                    <h3 className="font-medium text-emerald-800">Show Location</h3>
                    <p className="text-sm text-emerald-600">Display your location on your profile</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="show_location"
                      checked={privacySettings.show_location}
                      onChange={handlePrivacyChange}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-xl">
                  <div>
                    <h3 className="font-medium text-emerald-800">Show Email</h3>
                    <p className="text-sm text-emerald-600">Display your email address on your profile</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="show_email"
                      checked={privacySettings.show_email}
                      onChange={handlePrivacyChange}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Save Button */}
          <div className="flex justify-end pt-6 border-t border-emerald-200">
            <button
              onClick={saveProfile}
              disabled={loading || uploading}
              className="px-8 py-3 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Saving...' : uploading ? 'Uploading...' : 'Save Profile'}
            </button>
          </div>
        </div>
      </div>

      {/* High Resolution Image Modal */}
      {showImageModal && selectedImage && (
        <ImageModal
          imageUrl={enhanceImageUrl(selectedImage.url)}
          title={selectedImage.title}
          onClose={() => {
            setShowImageModal(false);
            setSelectedImage(null);
          }}
        />
      )}
    </div>
  );
};

export default Profile;